import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { openCookieSettings } from "@/components/CookieConsent";
import {
  ArrowRight, ShieldCheck, KeyRound, Lock, Eye, Globe, RefreshCw, Sparkles, Mail,
} from "lucide-react";

// Honest, substantiated security claims only — no unverifiable badges.
export const SECURITY_POINTS = [
  { icon: ShieldCheck, title: "Per-workspace isolation", body: "Every request is scoped to your own workspace using your signed session, so your data stays separated from every other customer." },
  { icon: KeyRound, title: "Hashed passwords", body: "Passwords are salted and hashed with bcrypt — never stored in plain text, and not readable by us." },
  { icon: Lock, title: "Encrypted in transit", body: "All data travelling between your browser and our servers is protected with TLS/HTTPS." },
  { icon: Eye, title: "Read-only support access", body: "Access is role-based. When support needs to look into a workspace, that \u201cview-as\u201d mode is strictly read-only and recorded." },
  { icon: Globe, title: "UK data protection by design", body: "Built to align with the UK GDPR and Data Protection Act 2018. Operated by Swishr Digital Ltd, a UK-registered company." },
  { icon: RefreshCw, title: "Your data, your control", body: "Ask us to export or permanently delete your workspace data at any time." },
];

const Point = ({ icon: Icon, title, body, i }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
    className="rounded-2xl border border-neutral-200 bg-white p-6">
    <div className="h-11 w-11 rounded-xl bg-[#EEF2F4] text-[#516670] flex items-center justify-center mb-4"><Icon className="h-5 w-5" /></div>
    <h3 className="font-heading text-lg font-semibold text-neutral-900">{title}</h3>
    <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{body}</p>
  </motion.div>
);

export default function Security() {
  return (
    <div className="min-h-screen bg-white text-neutral-900 font-sans" data-testid="security-page">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <Link to="/"><Logo className="h-14 sm:h-16" /></Link>
          <nav className="flex items-center gap-4 sm:gap-6 text-sm font-medium text-neutral-600">
            <Link to="/about" className="hidden sm:inline hover:text-neutral-900 transition-colors">About</Link>
            <Link to="/login" className="hover:text-neutral-900 transition-colors">Log in</Link>
            <Link to="/register"><Button size="sm" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-1.5">Get started <ArrowRight className="h-3.5 w-3.5" /></Button></Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-8 text-center">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs font-medium text-neutral-600 shadow-sm">
            <ShieldCheck className="h-3.5 w-3.5 text-[#516670]" /> Security &amp; privacy
          </div>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Built for discretion and trust</h1>
          <p className="mt-5 text-lg text-neutral-500 max-w-2xl mx-auto leading-relaxed">
            Family offices and private principals trust Swishr Central with sensitive information. Here is a plain-English summary of how we protect it — only what we actually do today.
          </p>
        </motion.div>
      </section>

      {/* Trust points */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SECURITY_POINTS.map((p, i) => <Point key={p.title} {...p} i={i} />)}
        </div>
      </section>

      {/* Data handling narrative */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-10">
        <div>
          <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-[#EEF2F4] text-[#516670] flex items-center justify-center"><Sparkles className="h-4 w-4" /></span><h2 className="font-heading text-lg font-semibold">Bounded, reviewable AI</h2></div>
          <p className="text-sm text-neutral-600 leading-relaxed">Our AI drafts your daily brief and turns notes into suggested tasks, but suggestions stay visibly separate from your confirmed records and are never saved without your review. We do not sell your data, and we do not permit AI providers to train their general models on your content where that control is available to us.</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-[#EEF2F4] text-[#516670] flex items-center justify-center"><Globe className="h-4 w-4" /></span><h2 className="font-heading text-lg font-semibold">How we handle your data</h2></div>
          <p className="text-sm text-neutral-600 leading-relaxed">We collect only what we need to run the service and process it in line with our <Link to="/privacy" className="text-[#516670] font-medium underline underline-offset-2">Privacy Policy</Link>. You can request a full export or permanent deletion of your workspace data at any time, and you have the right to complain to the UK Information Commissioner&rsquo;s Office (ICO).</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-[#EEF2F4] text-[#516670] flex items-center justify-center"><Mail className="h-4 w-4" /></span><h2 className="font-heading text-lg font-semibold">Responsible disclosure</h2></div>
          <p className="text-sm text-neutral-600 leading-relaxed">If you believe you have found a security issue, please contact us in writing at our registered office so we can investigate: Swishr Digital Ltd, Flat 6 Logan House, 1 Logan Place, London, United Kingdom, W8 6QN.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-[2rem] border border-neutral-200 bg-neutral-50/60 px-8 py-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Questions from your diligence team?</h2>
          <p className="mt-3 text-neutral-500 max-w-lg mx-auto">We&rsquo;re happy to walk family offices and their advisers through how Swishr Central keeps data isolated and protected.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register"><Button size="lg" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-2 w-full sm:w-auto px-7">Get started <ArrowRight className="h-4 w-4" /></Button></Link>
            <Link to="/privacy"><Button size="lg" variant="outline" className="rounded-full w-full sm:w-auto border-neutral-300 px-7">Read our Privacy Policy</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/"><Logo className="h-24 sm:h-28" /></Link>
          <p className="text-sm text-neutral-400 order-last sm:order-none">© 2026 Swishr Digital Ltd. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-neutral-500">
            <Link to="/about" className="hover:text-neutral-900 transition-colors">About</Link>
            <Link to="/security" className="hover:text-neutral-900 transition-colors">Security</Link>
            <Link to="/privacy" className="hover:text-neutral-900 transition-colors">Privacy Policy</Link>
            <Link to="/refunds" className="hover:text-neutral-900 transition-colors">Refund Policy</Link>
            <Link to="/terms" className="hover:text-neutral-900 transition-colors">Terms &amp; Conditions</Link>
            <button onClick={openCookieSettings} className="hover:text-neutral-900 transition-colors" data-testid="footer-cookie-settings">Cookie settings</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
